import { useState, useCallback, useMemo, useEffect } from 'react'
import { Plus, List } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { MasterRunbookFormDialog } from '../components/MasterRunbookFormDialog'
import { MasterRunbookDeleteDialog } from '../components/MasterRunbookDeleteDialog'
import { MasterRunbookViewDialog } from '../components/MasterRunbookViewDialog'
import { useMasterRunbooks, useDeleteMasterRunbook } from '../hooks'
import { createMasterRunbookColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { debounce } from '@/lib/utils'
import type { MasterRunbook } from '../api'
import type { ListParams } from '@/lib/api/types'

export function MasterRunbookListPage() {
    const { t } = useTranslation()
    const [selectedMasterRunbook, setSelectedMasterRunbook] = useState<MasterRunbook | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    // Debounce search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => {
        setDebouncedSearch(value)
        setPage(1) // Reset to page 1 when search changes
    }, 350), [])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    // Memoize params to prevent unnecessary re-renders
    const params: ListParams = useMemo(() => ({
        page,
        perPage: limit,
        search: debouncedSearch || undefined
    }), [page, limit, debouncedSearch])

    const { data: masterRunbooksResponse, isLoading } = useMasterRunbooks(params)
    const deleteMasterRunbook = useDeleteMasterRunbook()

    // Memoize data extraction to prevent unnecessary recalculations
    const apiResponse = useMemo(() => masterRunbooksResponse?.data, [masterRunbooksResponse])
    const masterRunbooks = useMemo(() => apiResponse?.data || [], [apiResponse])
    const totalMasterRunbooks = useMemo(() => 
        apiResponse?.pagination?.total || apiResponse?.total || 0, 
        [apiResponse]
    )

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((masterRunbook: MasterRunbook) => {
        setSelectedMasterRunbook(masterRunbook)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((masterRunbook: MasterRunbook) => {
        setSelectedMasterRunbook(masterRunbook)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((masterRunbook: MasterRunbook) => {
        setSelectedMasterRunbook(masterRunbook)
        setShowViewDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedMasterRunbook) {
            deleteMasterRunbook.mutate(selectedMasterRunbook.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedMasterRunbook(null)
                },
            })
        }
    }, [selectedMasterRunbook, deleteMasterRunbook])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export master runbooks to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createMasterRunbookColumns(handleEdit, handleDelete, handleView, t),
        [handleEdit, handleDelete, handleView, t]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('masterRunbooks.loadingMasterRunbooks')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('masterRunbooks.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('masterRunbooks.description')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('masterRunbooks.createMasterRunbook')}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-lg border bg-card p-6 md:col-start-2">
                        <div className="flex items-center space-x-2">
                            <List className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('masterRunbooks.totalMasterRunbooks')}</p>
                                <p className="text-2xl font-bold">{totalMasterRunbooks}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div 
                    className="overflow-x-auto" 
                    style={{ 
                        marginLeft: 'calc(var(--spacing-page) * -2)',
                        marginRight: 'calc(var(--spacing-page) * -2)',
                        paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                        paddingRight: 'calc(var(--spacing-page) * 0.05)',
                        width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                        maxWidth: '1200px',
                        margin: '0 auto'
                    }}
                >
                    <DataTable
                        columns={columns}
                        data={masterRunbooks}
                        searchKey="description"
                        searchPlaceholder={t('masterRunbooks.searchPlaceholder')}
                        searchValue={search}
                        onSearchChange={setSearch}
                        onExport={handleExport}
                        exportButtonText={t('masterRunbooks.export')}
                        enableRowSelection={false}
                        pagination={{
                            page,
                            limit,
                            total: totalMasterRunbooks,
                            onPageChange: setPage,
                            onPageSizeChange: setLimit,
                        }}
                    />
                </div>

                {/* Dialogs */}
                <MasterRunbookFormDialog
                    open={showCreateDialog}
                    onOpenChange={setShowCreateDialog}
                    masterRunbook={null}
                />

                <MasterRunbookFormDialog
                    open={showEditDialog}
                    onOpenChange={setShowEditDialog}
                    masterRunbook={selectedMasterRunbook}
                />

                <MasterRunbookDeleteDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    masterRunbook={selectedMasterRunbook}
                    onConfirm={handleDeleteConfirm}
                    isLoading={deleteMasterRunbook.isPending}
                />

                <MasterRunbookViewDialog
                    open={showViewDialog}
                    onOpenChange={setShowViewDialog}
                    masterRunbook={selectedMasterRunbook}
                />
            </div>
        </AdminRoute>
    )
}
